import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import type { SplitAmountRow, SplitModeTone } from "@apps/dashboard/lib/expense-splits";
import type { Locale } from "@project/i18n";

type ExpenseSplitBreakdownCardProps = {
  title: string;
  subtitle: string;
  rows: SplitAmountRow[];
  currency: string;
  locale: Locale;
  /** Pill a la derecha (p. ej. % del hogar / partes iguales); si se omite, no se muestra. */
  modeLabel?: string;
  /** Alineado con la pantalla de creación: `accent` = % hogar, `muted` = partes iguales. */
  modeLabelTone?: SplitModeTone;
};

export function ExpenseSplitBreakdownCard({
  title,
  subtitle,
  rows,
  currency,
  locale,
  modeLabel,
  modeLabelTone = "accent",
}: ExpenseSplitBreakdownCardProps): JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }

  const pillClass =
    modeLabelTone === "muted"
      ? "bg-outline-variant/15 text-on-surface-variant"
      : "bg-primary/12 text-primary";

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-gradient-to-br from-surface-container-high to-surface-container-low p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-headline text-title-md font-bold tracking-tight text-on-surface">
            {title}
          </h2>
          <p className="mt-1 max-w-xl text-label-md leading-relaxed text-on-surface-variant">
            {subtitle}
          </p>
        </div>
        {modeLabel ? (
          <span
            className={`shrink-0 rounded-full px-3 py-1 text-label-md font-medium ${pillClass}`}
          >
            {modeLabel}
          </span>
        ) : null}
      </div>
      <ul className="mt-5 space-y-4" aria-label={title}>
        {rows.map((row) => {
          const pct = row.shareBps / 100;
          const initial = row.name.trim().charAt(0).toUpperCase() || "?";
          return (
            <li
              key={row.userId}
              className="flex gap-3 rounded-xl bg-surface/80 p-3 outline outline-1 outline-outline-variant/10"
            >
              <div
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/15 font-headline text-sm font-bold text-primary"
                aria-hidden
              >
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="truncate font-medium text-on-surface">{row.name}</span>
                  <span className="shrink-0 font-headline text-lg font-bold text-primary">
                    {formatMinorAsCurrency(row.amountMinor, currency, locale)}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-outline-variant/20">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-300"
                      style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-label-md tabular-nums text-on-surface-variant">
                    {pct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
